import {
  AppCacheProvider,
  createEmotionCache
} from '@mui/material-nextjs/v15-pagesRouter'
import { Analytics } from '@vercel/analytics/next'
import { SpeedInsights } from '@vercel/speed-insights/next'
import type { NextPage } from 'next'
import type { UserConfig } from 'next-i18next'
import { appWithTranslation } from 'next-i18next'
import { DefaultSeo } from 'next-seo'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head.js'
import Router, { useRouter } from 'next/router'
import NextTopLoader from 'nextjs-toploader'
import type { ReactElement, ReactNode } from 'react'
import React, { useEffect } from 'react'
import { rubik } from 'src/constants/fonts'
import { seoConfig, seoConfigEnOverrides } from 'src/constants/next-seo-config'
import ApolloProvider from 'src/context/ApolloProvider'
import { isAutomatedTrafficUA } from 'src/utils/automatedTraffic'
import {
  guardCapabilityNavigation,
  isCapabilityRoute,
  redactTokenUrls,
  shouldSuppressCapabilityTelemetry
} from 'src/utils/capabilityPrivacy'
import {
  canonicalizeHydrationHtml,
  censusDelta,
  countBoundaryComments
} from 'src/utils/hydrationCanonicalize'
import type { LinkPreviewPageProps } from 'src/utils/linkPreviewSSR'
import logger from 'src/utils/loggerClient'
import { isInternalTraffic } from 'src/utils/pixels/internal'
import posthog from 'src/utils/pixels/posthog'
import { installStaleChunkReload } from 'src/utils/staleChunkReload'
import nextI18NextConfig from '../next-i18next.config.js'
import '../public/styles/global.css'
import AllThemeProviders from '../src/components/ThemeProvider'
import config from '../src/config'

// React 19 emits hydration warnings via console.error. In PROD the default
// onRecoverableError path calls console.error(errorObject) — so args[0] is an
// Error, not a string. Also catch the thrown #418 via window.error. The
// componentStack only exists if React attaches it to the error (React 19
// does this on hydration mismatches via error.componentStack / errorInfo).
let hydrationCaptureInstalled = false
// Set by reportHydration() the first time a real #418 fires. captureHydrationDiff
// reads this and only logs when it's true — otherwise the diff capture picks up
// post-mount state pop-ins (e.g. DesktopMegaMenu's active-link flip when the
// useIsClientMounted useEffect re-renders) as false-positive "mismatches",
// drowning the actual #418 culprits 14:1. Module-level so set/read between the
// window.error handler and the rAF callback survives across React commits.
let hydrationMismatchObserved = false
// Snapshot of __next.outerHTML at the moment the FIRST hydration error fires —
// captured inside reportHydration() before React's clientRender recovery
// completes, before any post-mount useEffects have flipped state (e.g.
// DesktopMegaMenu's useIsClientMounted → setMounted(true)). The previous
// post-RAF snapshot is captured AFTER recovery + effects, which makes the
// "first diff byte" almost always a downstream symptom (e.g. a pillar button
// that just became active) rather than the upstream trigger. This at-error
// snapshot is much closer to "what React's hydration walker actually saw"
// when it threw #418 — the real culprit is usually here.
let atErrorSnapshot: string | null = null

function extractMessage(arg: unknown): string {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return arg.message
  if (arg && typeof arg === 'object' && 'message' in arg) {
    const m = (arg as { message?: unknown }).message
    return typeof m === 'string' ? m : ''
  }
  return ''
}

function isHydrationMessage(msg: string): boolean {
  return (
    msg.includes('hydrat') ||
    msg.includes('Hydration') ||
    msg.includes('Minified React error #418') ||
    msg.includes('Minified React error #419') ||
    msg.includes('Minified React error #421') ||
    msg.includes('Minified React error #422') ||
    msg.includes('Minified React error #423') ||
    msg.includes('Minified React error #425') ||
    msg.includes('did not match') ||
    msg.includes('react.dev/errors/418') ||
    msg.includes('react.dev/errors/419') ||
    msg.includes('react.dev/errors/421') ||
    msg.includes('react.dev/errors/422') ||
    msg.includes('react.dev/errors/423') ||
    msg.includes('react.dev/errors/425')
  )
}

function reportHydration(source: string, args: unknown[], err?: unknown) {
  // Flip the gate the moment we know a real hydration error happened.
  // captureHydrationDiff() will only emit its rich SSR-vs-CSR snippet log when
  // this is true, so the diff stops drowning in post-mount flip-flops.
  hydrationMismatchObserved = true
  // Snapshot the DOM SYNCHRONOUSLY before any further work runs — this fires
  // from React's defaultOnRecoverableError (console.error wrap) or from the
  // window.error event, both of which dispatch before clientRender recovery
  // completes and well before mounted-state useEffects commit their re-renders.
  // The post-RAF snapshot in captureHydrationDiff is captured AFTER all that,
  // so it shows post-effect symptoms (e.g. nav pillar buttons that just became
  // active) — that drowned the real trigger 27:1 on /pension-estacionamiento/
  // flotillas in May 2026. Keep only the FIRST at-error snapshot per page load
  // — subsequent errors are downstream cascades that overwrite the trigger.
  if (atErrorSnapshot === null && typeof window !== 'undefined') {
    try {
      const node = document.getElementById('__next')
      if (node) atErrorSnapshot = node.outerHTML
    } catch {
      // never let snapshot capture break the error handler
    }
  }
  try {
    const first = args[0]
    const message = extractMessage(first)
    const errAny = (err ?? first) as
      (Error & { componentStack?: string; digest?: string }) | undefined
    const stringArgComponentStack = args.find(
      (a): a is string => typeof a === 'string' && /\n\s*at\s/.test(a)
    )
    logger.error('hydration_mismatch', {
      source,
      message,
      errorStack: errAny instanceof Error ? errAny.stack : undefined,
      errorName: errAny instanceof Error ? errAny.name : undefined,
      errorComponentStack: errAny?.componentStack,
      digest: errAny?.digest,
      componentStack: stringArgComponentStack,
      argsCount: args.length,
      args0Type: first instanceof Error ? 'Error' : typeof first,
      url: window.location.href,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent
    })
    // Mirror to PostHog. capture_exceptions: true autocaptures
    // window.onerror but misses React 19's defaultOnRecoverableError →
    // reportError() path for hydration mismatches in prod. Manual capture
    // makes #418 visible in PostHog Error Tracking with the matching
    // session recording, which is where we get the visual evidence
    // Datadog can't provide. runOrQueue handles the case where
    // hydration fires before the 2 s PostHog fuse — calls buffer and
    // drain on init.
    posthog.captureException(
      errAny ?? new Error(message || 'hydration_mismatch'),
      {
        source,
        digest: errAny?.digest,
        componentStack: errAny?.componentStack ?? stringArgComponentStack,
        pathname: window.location.pathname,
        // Tag so we can build a dedicated PostHog issue + cohort.
        $exception_personURL: undefined,
        spotme_error_kind: 'hydration_mismatch'
      }
    )
  } catch {
    // never let logging break console.error or window handlers
  }
}

function installHydrationCapture() {
  if (hydrationCaptureInstalled || typeof window === 'undefined') return
  hydrationCaptureInstalled = true

  // 1. Wrap console.error to catch React's recoverable error path.
  // eslint-disable-next-line no-console
  const origError = console.error
  // eslint-disable-next-line no-console
  console.error = (...args: unknown[]) => {
    const msg = extractMessage(args[0])
    // Skip our own report: locally `logger` IS `console`, so
    // `logger.error('hydration_mismatch', …)` lands back here, matches
    // 'hydrat' and recurses until the stack overflows (2026-10-28).
    if (msg !== 'hydration_mismatch' && isHydrationMessage(msg)) {
      reportHydration('console.error', args)
    }
    origError.apply(console, args)
  }

  // 2. Catch the thrown #418 via window error events (React's
  // reportGlobalError dispatches an ErrorEvent before console.error).
  window.addEventListener(
    'error',
    (e: ErrorEvent) => {
      const msg = extractMessage(e.error) || e.message || ''
      if (isHydrationMessage(msg)) {
        reportHydration('window.error', [e.error ?? e.message], e.error)
      }
    },
    true
  )
}

// =============================================================================
// Hydration diff snapshot — captures the actual SSR HTML and the first
// post-hydration HTML, finds the first character where they diverge, and
// logs ~240 chars of context to Datadog. Temporary instrumentation for the
// #418 hunt; remove once the offender is identified.
// =============================================================================
let hydrationDiffSnapshot: string | null = null
if (typeof window !== 'undefined') {
  // _app.tsx evaluates AFTER Next streams the SSR HTML into the DOM but
  // BEFORE React.hydrateRoot wires up React state. So __next.outerHTML at
  // this exact moment IS the server-rendered HTML.
  const ssrRoot = document.getElementById('__next')
  if (ssrRoot) hydrationDiffSnapshot = ssrRoot.outerHTML
}

function captureHydrationDiff() {
  if (typeof window === 'undefined' || !hydrationDiffSnapshot) return
  // Gate: only log if a real #418 actually fired. Without this, every page
  // load with a post-mount visual change (DesktopMegaMenu active-link
  // pop-in, lazy-loaded providers, embla translate3d, etc.) reports a
  // "diff" — drowning the real culprits 14:1 in Datadog.
  if (!hydrationMismatchObserved) {
    hydrationDiffSnapshot = null
    return
  }
  const csrRoot = document.getElementById('__next')
  if (!csrRoot) return
  const ssrHtml = hydrationDiffSnapshot
  // Prefer the at-error snapshot — captured synchronously inside reportHydration
  // before clientRender recovery + mounted-state effects had a chance to
  // overwrite the trigger location with downstream symptoms. Falls back to the
  // post-RAF DOM only if reportHydration didn't manage to snapshot (e.g.
  // running before _app's installHydrationCapture had a chance, or DOM threw).
  const usedAtErrorSnapshot = atErrorSnapshot !== null
  const csrHtmlRaw = atErrorSnapshot ?? csrRoot.outerHTML
  // Free the snapshots — they can be ~200 KB on listing pages
  hydrationDiffSnapshot = null
  atErrorSnapshot = null

  // Canonicalize BOTH sides identically before the byte-diff — every rule in
  // src/utils/hydrationCanonicalize.ts removes a known-benign ATTRIBUTE-level
  // re-serialization artifact (style whitespace/quoting, zero units, attribute
  // order, Safari's `inset` shorthand). React only throws #418 on TEXT or
  // STRUCTURAL divergence, so a diff that still lands on an attribute means the
  // canonicalizer has another gap — widen it there (it has tests), never "fix"
  // the styled element. History: docs/claude/hydration.md → "Recorder".
  // Census + comment counts come from the RAW snapshots — tag counting doesn't
  // care about attribute formatting, and the comment counts must be read BEFORE
  // canonicalizeHydrationHtml strips them.
  const structuralDelta = censusDelta(ssrHtml, csrHtmlRaw)
  const ssrComments = countBoundaryComments(ssrHtml)
  const csrComments = countBoundaryComments(csrHtmlRaw)

  const ssrHtmlN = canonicalizeHydrationHtml(ssrHtml)
  const csrHtml = canonicalizeHydrationHtml(csrHtmlRaw)
  if (ssrHtmlN === csrHtml) return

  // Walk forward to find the first differing char (on the style-canonicalized
  // strings, so a benign inline-style re-serialization never reads as the
  // trigger).
  let i = 0
  const minLen = Math.min(ssrHtmlN.length, csrHtml.length)
  while (i < minLen && ssrHtmlN.charCodeAt(i) === csrHtml.charCodeAt(i)) i++

  const ctxBefore = 80
  const ctxAfter = 160
  const start = Math.max(0, i - ctxBefore)
  // Slice from the style-canonicalized strings so the snippet aligns with `i`.
  const ssrSnippet = ssrHtmlN.slice(start, i + ctxAfter)
  const csrSnippet = csrHtml.slice(start, i + ctxAfter)

  // Aggregatable facets: scan back from the diff offset to the nearest opening
  // tag on each side, so Datadog can group hydration mismatches by element
  // without anyone copying snippets out of the UI. Captures the element NAME
  // and the first ~24 chars of attributes (id/class are the load-bearing part
  // of identifying the offender; full attribute payloads stay in `ssrSnippet`).
  const extractNearestTag = (
    html: string,
    offset: number
  ): { tag: string; attrPreview: string } => {
    const lt = html.lastIndexOf('<', offset)
    if (lt < 0) return { tag: '', attrPreview: '' }
    const slice = html.slice(lt, lt + 120)
    const m = slice.match(/^<\/?([A-Za-z][A-Za-z0-9-]*)([^>]{0,80})/)
    if (!m) return { tag: '', attrPreview: '' }
    return { tag: m[1].toLowerCase(), attrPreview: (m[2] ?? '').slice(0, 24) }
  }
  const ssrNearTag = extractNearestTag(ssrHtmlN, i)
  const csrNearTag = extractNearestTag(csrHtml, i)

  // Classify known-unfixable third-party DOM mutations + structural
  // SSR/CSR drifts that we've decided not to chase. Tag them with a
  // `knownFalsePositive` facet so we can filter `-@knownFalsePositive:*`
  // in the Datadog dashboard (real-trigger view) while still keeping
  // volume on a separate dashboard if needed (third-party-behavior view).
  const isInsideNoscript = (html: string, offset: number): boolean => {
    // Walk back from the diff offset; if we hit `<noscript` before the
    // matching `</noscript>` (or before any earlier `</noscript>`), the
    // diff is inside a noscript element.
    const openIdx = html.lastIndexOf('<noscript', offset)
    if (openIdx < 0) return false
    const closeIdx = html.lastIndexOf('</noscript>', offset)
    return closeIdx < openIdx
  }
  // Detect inline `background-image: url(...)` SSR/CSR drift. Browsers
  // normalize `url(/path)` to `url("/path")` (add quotes) on the CSR DOM
  // serialization plus whitespace/semi cleanup. ReactDOMServer emits the
  // raw user-provided string. There is no JSX-level workaround — the only
  // way to fully eliminate this is to drop the inline `style` entirely
  // (impossible for dynamic URLs without sacrificing the asset itself).
  // The visible UX cost on real users is one clientRender fallback frame
  // on first paint, then the page works normally. Tag and move on.
  const isBackgroundImageDrift = (
    ssrSlice: string,
    csrSlice: string
  ): boolean => {
    // SSR side will contain `background-image:url(` (compact, no quotes).
    // CSR side will contain `background-image: url(` (space) and usually
    // also escaped quotes `url(&quot;` or `url("` from DOM serialization.
    const ssrHas = ssrSlice.indexOf('background-image:url(') !== -1
    const csrHas =
      csrSlice.indexOf('background-image: url(') !== -1 ||
      csrSlice.indexOf('background-image:url(&quot;') !== -1 ||
      csrSlice.indexOf('background-image: url(&quot;') !== -1
    return ssrHas && csrHas
  }
  let knownFalsePositive: string | undefined
  if (isInsideNoscript(ssrHtmlN, i) || isInsideNoscript(csrHtml, i)) {
    // FB IAB (FBAN/FBIOS) and iOS GSA both strip the FB pixel + adjacent
    // tracking pixels from inside <noscript> before exposing the DOM to
    // React. CLAUDE.md documents this as the canonical
    // `suppressHydrationWarning` case — but suppressHydrationWarning only
    // silences React's console warning, not our captureHydrationDiff
    // recorder. Without this classification the FB IAB noscript-pixel
    // mismatch fires ~750 events/24h drowning out real triggers.
    knownFalsePositive = 'noscript_tracker_strip'
  } else if (isBackgroundImageDrift(ssrSnippet, csrSnippet)) {
    // IconMenu logo + SpecialistCard avatar use `style={{ backgroundImage:
    // 'url(...)' }}` after we exhausted Phase 1-3 fixes. The remaining
    // drift is the browser's URL-quoting + whitespace normalization on the
    // CSR DOM serialization — structural in React 19 + browser
    // CSSOMString.toString(), not a code-level bug. CSR clientRender
    // fallback on first paint is the visible cost; the page works
    // normally after. See CLAUDE.md "Trigger D Phase 4 — accepted residual".
    knownFalsePositive = 'inline_background_image_url_drift'
  }

  // Filter automated traffic at the recorder layer. Bots, SEO audit tools,
  // and Lighthouse runs trigger #418 because they render pages at unusual
  // timing / with stripped JS / with different attribute-serialization
  // quirks — but they have NO real-user UX impact. Per Datadog 7-day
  // analysis (2026-06-02): ~60% of `hydration_diff` volume comes from
  // these UAs. Skipping them at the recorder cuts dashboard noise without
  // changing actual page behavior (CSR clientRender fallback still
  // happens regardless of whether we log it).
  //
  // This is ONE of two client-error pipelines. The Datadog browser-logs SDK
  // (src/utils/loggerClient.ts, forwardErrorsToLogs) independently catches
  // React's raw `Minified React error #418` console.error and applies the
  // SAME isAutomatedTrafficUA skip — both share AUTOMATED_TRAFFIC_UA_REGEX so
  // the bot policy stays in lockstep. See spotme-client/CLAUDE.md → "Two
  // client-error pipelines for #418". Don't add a third #418 filter.
  const ua = navigator.userAgent
  const isAutomatedTraffic = isAutomatedTrafficUA(ua)
  if (isAutomatedTraffic) {
    // Skip logging entirely for automated traffic. We don't even tag this
    // as a falsePositive because the volume is high enough that even the
    // tagged-and-filtered approach would still cost Datadog ingest. If we
    // later need to track bot #418 specifically (e.g. SEO crawl budget
    // analysis), bring back a sampled subset via a `bot_sample` facet.
    return
  }

  try {
    logger.error('hydration_diff', {
      // PRIMARY DIAGNOSTIC — read this before `firstDiffIndex`. A compact,
      // groupable structural delta like `INPUT:-1,SPAN:-4` (negative = the SSR
      // had it, the at-error DOM did not). React throws #418 only on TEXT or
      // STRUCTURAL divergence, and unlike the byte-diff this survives boundary-
      // comment loss, style re-serialization, attribute order and emotion
      // <style> relocation. It named Trigger O in one shot. An EMPTY value
      // means no element differs ⇒ it is a text mismatch, so read the snippets.
      censusDelta: structuralDelta,
      // Boundary comments are server-render-only. `csrBoundaryComments: 0`
      // alongside a non-zero SSR count is the fingerprint of a full clientRender
      // having already happened — expected after #418, and the reason these are
      // stripped before diffing (recorder gap #5).
      ssrBoundaryComments: ssrComments,
      csrBoundaryComments: csrComments,
      firstDiffIndex: i,
      ssrLen: ssrHtmlN.length,
      csrLen: csrHtml.length,
      ssrSnippet,
      csrSnippet,
      ssrTag: ssrNearTag.tag,
      csrTag: csrNearTag.tag,
      ssrTagAttrs: ssrNearTag.attrPreview,
      csrTagAttrs: csrNearTag.attrPreview,
      // Tells whether the CSR side of the diff is the at-error snapshot
      // (closer to React's hydration-failure moment, more likely the real
      // trigger) or the post-RAF post-recovery DOM (more likely a downstream
      // symptom). Filter Datadog by capturedAt:at_error to isolate triggers.
      capturedAt: usedAtErrorSnapshot ? 'at_error' : 'post_recovery',
      // Known-unfixable third-party DOM mutations get a facet so we can
      // filter `-@knownFalsePositive:*` in Datadog to see only real triggers
      // without losing third-party-behavior volume on a separate dashboard.
      knownFalsePositive,
      pathname: window.location.pathname,
      search: window.location.search,
      url: window.location.href,
      userAgent: navigator.userAgent
    })
  } catch {
    // never let logging break the page
  }
}

// Register before any analytics components mount. A SPA entry would expose the
// token to already-running history observers even if Scripts were unmounted.
if (typeof window !== 'undefined') {
  Router.events.on('routeChangeStart', (url: string) => {
    guardCapabilityNavigation(url, window.location.href, (destination) => {
      window.location.assign(destination)
    })
  })
}

installHydrationCapture()
// One-shot reload on stale-deploy chunk failures, independent of whether
// Datadog/PostHog initialised. See src/utils/staleChunkReload.ts.
installStaleChunkReload()

// Lazy-load non-critical providers to reduce _app bundle size
const ErrorBoundary = dynamic(() => import('../src/components/ErrorBoundary'))
// Bot-only (link unfurlers) — lazy so it never weighs on the real-user bundle
const LinkPreviewShell = dynamic(
  () => import('../src/components/LinkPreviewShell')
)
const Scripts = dynamic(() => import('../src/components/Scripts'))
const UTMs = dynamic(() => import('../src/components/UTMs'))
const LandingViewTracker = dynamic(
  () => import('../src/components/LandingViewTracker')
)
const LandingEngagementTracker = dynamic(
  () => import('../src/components/LandingEngagementTracker')
)
const AuthProvider = dynamic(
  () => import('../src/context/AuthProvider/provider')
)
const CurrencyAndLanguageProvider = dynamic(
  () => import('../src/context/CurrencyAndLanguageProvider')
)
const LoaderProvider = dynamic(() => import('../src/context/LoaderProvider'))
const LoggerProvider = dynamic(() => import('../src/context/LoggerProvider'))
const SnackbarProvider = dynamic(() => import('../src/context/SnackProvider'))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { mode, debug }: { mode: any; debug: boolean } = config.isProd
  ? { mode: 'production', debug: false }
  : { mode: 'development', debug: true }

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getLayout?: (page: ReactElement, pageProps: any) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const emptyInitialI18NextConfig: UserConfig = {
  i18n: {
    defaultLocale: nextI18NextConfig.i18n.defaultLocale,
    locales: nextI18NextConfig.i18n.locales
  }
}

function MyApp(props: AppPropsWithLayout) {
  const { Component, pageProps } = props
  const getLayout = Component.getLayout ?? ((page) => page)
  const { locale, pathname } = useRouter()
  const privatePage = isCapabilityRoute(pathname)
  const isEnglish = locale === 'en'

  // Meta-only shell for link-preview unfurlers on auth-gated pages — set by
  // getLinkPreviewShellProps (src/utils/linkPreviewSSR.ts) in place of the
  // auth 307. Replaces the page component entirely: no page data was fetched.
  const linkPreview = (pageProps as Partial<LinkPreviewPageProps>).__linkPreview

  // Run the SSR-vs-CSR diff after React commits the first hydration pass.
  // requestAnimationFrame defers until after layout but still on the first
  // frame; any earlier and React may not have finished restoring DOM yet.
  useEffect(() => {
    const raf = requestAnimationFrame(captureHydrationDiff)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <>
      <NextTopLoader
        color="#00A4FF"
        height={2}
        shadow={false}
        showSpinner={false}
      />
      {!privatePage && <Scripts />}
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        {/* eslint-disable-next-line react/no-unknown-property */}
        <style jsx global>{`
          html {
            font-family: ${rubik.style.fontFamily};
          }
        `}</style>
      </Head>
      <AppCacheProvider {...props} emotionCache={createEmotionCache()}>
        <AllThemeProviders>
          <LoggerProvider>
            <ErrorBoundary>
              <ApolloProvider>
                <LoaderProvider>
                  <SnackbarProvider>
                    <AuthProvider>
                      <CurrencyAndLanguageProvider>
                        <DefaultSeo
                          {...seoConfig}
                          {...(isEnglish ? seoConfigEnOverrides : {})}
                          additionalMetaTags={
                            isEnglish
                              ? [
                                  ...(seoConfig.additionalMetaTags?.filter(
                                    (t) => t.name !== 'robots'
                                  ) ?? []),
                                  {
                                    name: 'robots',
                                    content: 'noindex, follow'
                                  }
                                ]
                              : seoConfig.additionalMetaTags
                          }
                        />
                        {linkPreview ? (
                          <LinkPreviewShell {...linkPreview} />
                        ) : (
                          getLayout(<Component {...pageProps} />, pageProps)
                        )}

                        {!privatePage && (
                          <>
                            <UTMs />
                            <LandingViewTracker />
                            <LandingEngagementTracker />
                          </>
                        )}
                        {config.isProd && !privatePage && (
                          <>
                            <Analytics
                              mode={mode}
                              debug={debug}
                              beforeSend={(e) =>
                                isInternalTraffic() ||
                                shouldSuppressCapabilityTelemetry(e)
                                  ? null
                                  : redactTokenUrls(e)
                              }
                            />
                            <SpeedInsights
                              debug={debug}
                              beforeSend={(e) =>
                                shouldSuppressCapabilityTelemetry(e)
                                  ? null
                                  : redactTokenUrls(e)
                              }
                            />
                          </>
                        )}
                      </CurrencyAndLanguageProvider>
                    </AuthProvider>
                  </SnackbarProvider>
                </LoaderProvider>
              </ApolloProvider>
            </ErrorBoundary>
          </LoggerProvider>
        </AllThemeProviders>
      </AppCacheProvider>
    </>
  )
}

export default appWithTranslation(React.memo(MyApp), emptyInitialI18NextConfig)
